// ¿VA A PODER VERLO EL EQUIPO? — se pregunta AL SUBIR, no al abrirlo.
//
// Ticket de Gabriel De Silva (2026-09-02): *"el visor de video sale en negro"*.
// La primera mitad se arreglo el 2026-09-02 (quitar el `autoPlay`, y que el
// visor diga el error en cristiano en vez de quedarse negro). Esta es la otra
// mitad, la que Lissette dejo pendiente: *"lo del tema del video grabado por
// iPhone lo entiendo, pero el problema al momento de abrir el video"*.
//
// EL PROBLEMA REAL. Un iPhone graba por defecto en "Alta eficiencia" (HEVC /
// H.265). Ese video se ve perfecto en el propio iPhone y en un Mac — o sea que
// QUIEN LO SUBE no nota nada — y NO se ve en Chrome sobre Windows, que es lo
// que usa el resto del equipo. El fallo aparece dias despues, en la pantalla de
// otra persona, cuando ya no hay forma de volver a grabarlo.
//
// POR QUE NO SE CONVIERTE EN EL SERVIDOR. Convertir un video es tener la CPU al
// 100% durante medio minuto o mas. El servidor de Deskte es de 2 nucleos, y el
// 2026-09-03 quedo MEDIDO lo que pasa cuando una sola tarea pesada lo ocupa:
// todo lo demas se puso en cola y termino de golpe 9 segundos despues (E-188).
// Meter conversion de video en esa misma caja seria repetir ese error a
// proposito y multiplicado. Hacerlo bien pide una maquina aparte, y eso es una
// decision de gasto de Lissette, no un arreglo tecnico que se cuela en un PR.
//
// LO QUE SI SE PUEDE, Y ES MEJOR: avisar en el momento de subirlo, cuando la
// persona TIENE el telefono en la mano y puede volver a grabarlo o compartirlo
// en el formato compatible. Un aviso a tiempo evita el problema; uno tarde solo
// lo explica.
//
// COMO SE SABE EL CODEC. En un .mp4/.mov el codec vive en la caja `moov`, que
// el iPhone escribe al FINAL del archivo. Por eso se mira la cola y no la
// cabeza — y se busca DENTRO de `moov`, no en todo el archivo: los cuatro
// bytes "hvc1" pueden salir por casualidad en el video comprimido, y un aviso
// falso ensena a la gente a ignorar los avisos.

/** Cuanto se lee de cada punta. La caja `moov` de un video normal cabe de sobra. */
export const BYTES_DE_LA_COLA = 4 * 1024 * 1024;
export const BYTES_DE_LA_CABEZA = 256 * 1024;

/** Contenedores que Chrome no reproduce, se grabe como se grabe. */
const CONTENEDORES_MALOS = ['mkv', 'avi', 'wmv', 'flv', 'mpg', 'mpeg', '3gp'];

export const extensionDe = (nombre) => {
  const m = /\.([a-z0-9]+)$/i.exec(String(nombre || '').trim());
  return m ? m[1].toLowerCase() : '';
};

/** Busca los 4 bytes de `aguja` en `bytes`, desde `desde`. -1 si no esta. */
export function buscarFourcc(bytes, aguja, desde = 0) {
  if (!bytes || bytes.length < 4 || !aguja || aguja.length !== 4) return -1;
  const a = aguja.charCodeAt(0), b = aguja.charCodeAt(1);
  const c = aguja.charCodeAt(2), d = aguja.charCodeAt(3);
  for (let i = Math.max(0, desde); i <= bytes.length - 4; i++) {
    if (bytes[i] === a && bytes[i + 1] === b && bytes[i + 2] === c && bytes[i + 3] === d) return i;
  }
  return -1;
}

/**
 * El codec del video, leido de la caja `moov`.
 *
 * Se busca `moov` PRIMERO y el codec DESPUES de ahi. Buscar "hvc1" en todo el
 * archivo daria falsos positivos: son cuatro bytes cualesquiera, y en unos
 * megas de video comprimido pueden aparecer por casualidad. Un aviso falso es
 * peor que ninguno — ensena a la gente a ignorarlos.
 *
 * @returns {'hevc'|'h264'|null} null = no se pudo saber (no es motivo de aviso)
 */
export function codecDelMoov(bytes) {
  if (!bytes || !bytes.length) return null;
  const moov = buscarFourcc(bytes, 'moov');
  if (moov < 0) return null;
  const hevc = buscarFourcc(bytes, 'hvc1', moov) >= 0 || buscarFourcc(bytes, 'hev1', moov) >= 0;
  if (hevc) return 'hevc';
  const h264 = buscarFourcc(bytes, 'avc1', moov) >= 0 || buscarFourcc(bytes, 'avc3', moov) >= 0;
  if (h264) return 'h264';
  return null;
}

/**
 * ¿Hay algo que avisar sobre este video?
 *
 * @param {string} nombre  nombre del archivo
 * @param {Uint8Array} cola  ultimos bytes del archivo (donde vive `moov`)
 * @returns {{avisar:boolean, motivo:'contenedor'|'hevc'|null, extension:string, codec:string|null}}
 */
export function revisarVideo(nombre, cola) {
  const extension = extensionDe(nombre);
  if (CONTENEDORES_MALOS.includes(extension)) {
    return { avisar: true, motivo: 'contenedor', extension, codec: null };
  }
  const codec = codecDelMoov(cola);
  if (codec === 'hevc') return { avisar: true, motivo: 'hevc', extension, codec };
  // Sin dato no se avisa: molestar a quien sube un video que SI se ve es la
  // forma mas rapida de que nadie lea el aviso el dia que importa.
  return { avisar: false, motivo: null, extension, codec };
}

/** El aviso, en cristiano y con la salida concreta. */
export function textoDelAviso(revision, es = true) {
  if (!revision || !revision.avisar) return '';
  if (revision.motivo === 'contenedor') {
    return es
      ? `Los archivos .${revision.extension} no se ven en el navegador: a tu equipo le saldrá la pantalla en negro. Conviértelo a .mp4 antes de subirlo, o súbelo igual y quien lo necesite tendrá que descargarlo.`
      : `.${revision.extension} files don't play in the browser: your team will get a black screen. Convert it to .mp4 first, or upload it anyway and whoever needs it will have to download it.`;
  }
  return es
    ? 'Este video está grabado en "Alta eficiencia" (HEVC), el formato que trae el iPhone de fábrica. Tú lo ves bien, pero a quien lo abra en Windows con Chrome le saldrá la pantalla en negro. Para arreglarlo: en el iPhone, Ajustes → Cámara → Formatos → "Más compatible", y vuelve a grabarlo. También sirve compartirlo primero por WhatsApp y subir ese archivo, que ya viene convertido.'
    : 'This video is recorded in “High Efficiency” (HEVC), the iPhone default. It looks fine to you, but anyone opening it on Windows with Chrome will get a black screen. To fix it: on the iPhone, Settings → Camera → Formats → “Most Compatible”, and record it again. Sharing it through WhatsApp first and uploading that file also works — that one comes converted.';
}

/**
 * Lee la cola del archivo y lo revisa. Nunca lanza: si no se puede leer, se
 * devuelve "no avisar" — un archivo ilegible aqui NO es motivo para frenar una
 * subida que a lo mejor funciona perfectamente.
 */
export async function revisarArchivoDeVideo(file) {
  const vacio = { avisar: false, motivo: null, extension: extensionDe(file && file.name), codec: null };
  try {
    if (!file || typeof file.slice !== 'function') return vacio;
    const desde = Math.max(0, (file.size || 0) - BYTES_DE_LA_COLA);
    const trozo = await file.slice(desde).arrayBuffer();
    return revisarVideo(file.name, new Uint8Array(trozo));
  } catch {
    return vacio;
  }
}
